import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AzureOnboardingStatusStatusEnum, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import CopyableCode from '@/components/shared/copyableCode';
import { SupportedRegions } from '@/components/vaults/editBackupVaultPanel';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupOwnCloudState } from './SetupOwnCloudState';
import { BulletItem } from './bulletItem';
import { extractErrorMessage } from './errorHandling';
import { FormTextField } from './formTextField';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';

export const ConnectRestoreAccount = (props: StepProps<SetupOwnCloudState>) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const dal = useDAL();
  const [error, setError] = useState<Error | null>(null);

  const code = `\
  module "azure_restore" {
    source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/restore.zip"
  
    eon_account_id                   = "${props.wizardState.eonAccountId ?? ''}"
    project_id                       = "${props.wizardState.projectId ?? ''}"
    eon_backup_app_id                = "${props.wizardState.eonBackupApplicationId ?? ''}"
    customer_backup_app_id           = "${props.wizardState.customerBackupAppId ?? ''}"
  }`;

  const validateInput = () => {
    if (!props.wizardState.tenantId) {
      return false;
    }

    if (!props.wizardState.restoreSubscriptionId) {
      return false;
    }

    if (!props.wizardState.restoreManagedIdentityRegion) {
      return false;
    }

    if (
      props.wizardState.restoreConnectType === 'resourceGroup' &&
      !props.wizardState.restoreResourceGroupName
    ) {
      return false;
    }

    return true;
  };

  const getValidationError = () => {
    if (!props.wizardState.tenantId) {
      return new Error('Tenant ID is required');
    }

    if (!props.wizardState.restoreSubscriptionId) {
      return new Error('Subscription ID is required');
    }

    if (!props.wizardState.restoreManagedIdentityRegion) {
      return new Error('Managed identity region is required');
    }

    if (
      props.wizardState.restoreConnectType === 'resourceGroup' &&
      !props.wizardState.restoreResourceGroupName
    ) {
      return new Error('Resource group name is required');
    }

    return null;
  };

  const onNextClick = async () => {
    const validationError = getValidationError();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsDeploying(true);
    try {
      const response = await dal.onboarding.restoreAccount.deploy({
        tenantId: props.wizardState.tenantId!,
        restoreSubscriptionId: props.wizardState.restoreSubscriptionId!,
        restoreResourceGroupName: props.wizardState.restoreResourceGroupName!,
        location: props.wizardState.restoreManagedIdentityRegion!,
      });

      // Check if the response has a status field and it's not succeeded
      if (
        response &&
        response.status !== AzureOnboardingStatusStatusEnum.Succeeded
      ) {
        console.log(response);
        setError(new Error('Failed to deploy source account'));
      } else {
        setIsDeploying(false);
        await props.finish();
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={3}
        stepperLabels={props.stepperLabels}
        canGoNext={() => {
          return !isDeploying && validateInput();
        }}
        nextButtonText={'Connect'}
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingTop: '78px' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='65px' width='100%' height='auto'>
          <BulletItem
            title='Fill in the details to generate your Terraform module,
                      then review and deploy it in your environment.'
            leftContent={
              <>
                <Stack gap='24px'>
                  <FormTextField
                    title='Tenant ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.tenantId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        tenantId: event.target.value,
                      })
                    }
                  />
                  <FormTextField
                    title='Subscription ID'
                    placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                    value={props.wizardState.restoreSubscriptionId || ''}
                    onChange={(event) =>
                      props.setWizardState({
                        ...props.wizardState,
                        restoreSubscriptionId: event.target.value,
                      })
                    }
                  />
                  <FormControl>
                    <FormLabel>
                      <Typography variant='subtitle1'>
                        Allow Eon to restore to
                      </Typography>
                    </FormLabel>
                    <ToggleButtonGroup
                      sx={{ width: 'max-content' }}
                      exclusive
                      value={props.wizardState.restoreConnectType}
                      onChange={(_, value) => {
                        props.setWizardState({
                          ...props.wizardState,
                          restoreConnectType: value,
                          ...(value === 'subscription' && {
                            restoreResourceGroupName: '',
                          }),
                        });
                      }}
                    >
                      <ToggleButton value='subscription'>
                        All resource groups
                      </ToggleButton>
                      <ToggleButton value='resourceGroup'>
                        Specific resource group
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                  {props.wizardState.restoreConnectType === 'resourceGroup' && (
                    <FormTextField
                      title='Resource group name'
                      placeholder='Example: eon-123'
                      value={props.wizardState.restoreResourceGroupName || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          restoreResourceGroupName: event.target.value,
                        })
                      }
                    />
                  )}
                </Stack>
              </>
            }
            rightContent={<CopyableCode title='main.tf' code={code} />}
          />
          <Divider sx={{ borderStyle: 'dashed', width: '40%', my: '50px' }} />
          <BulletItem
            leftContent={
              <>
                <Stack>
                  <FormControl size='small' className='w-full'>
                    <FormLabel>
                      <Typography variant='subtitle1'>
                        Managed identity region
                      </Typography>
                    </FormLabel>
                    <Select
                      data-testid='region'
                      value={props.wizardState.restoreManagedIdentityRegion}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          restoreManagedIdentityRegion: event.target.value,
                        })
                      }
                    >
                      {SupportedRegions[Provider.Azure]?.map((region, i) => {
                        return (
                          <MenuItem
                            value={region}
                            key={i}
                            data-testid={`region-${region}`}
                          >
                            {region}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Stack>
              </>
            }
          />
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
